import type { ReactNode } from 'react';
import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '../primitives';
import { useIsPhone } from '../hooks/useIsPhone';
// The shared helper: a local copy here wrote `width: 1` in `sx`, which MUI
// reads as 100%, so the hidden span was a row-wide box that could overflow.
import { visuallyHidden } from '../theme/focus';

/** One column of a {@link ResponsiveTable}. */
export interface ResponsiveColumn<Row> {
  key: string;
  /** Header text; on a phone, the label beside the value. */
  label: string;
  cell: (row: Row) => ReactNode;
  align?: 'left' | 'center' | 'right';
  /**
   * The column that names the row — a contractor, an offence code. On a phone
   * it is the card's heading, so the row is identifiable without scrolling.
   */
  primary?: boolean;
  /**
   * The row's buttons (Edit, Retire). On a phone they sit at the foot of the
   * card, unlabelled, so they are never pushed off-screen.
   */
  actions?: boolean;
  /** Never wrap. Use for amounts, dates and short codes. */
  nowrap?: boolean;
  /** Minimum width of the cell in the table layout (e.g. an input column). */
  minWidth?: number;
}

export interface StackedRowsProps<Row> {
  rows: Row[];
  columns: ResponsiveColumn<Row>[];
  getRowKey: (row: Row) => string;
  /**
   * Opens the row (e.g. a detail drawer). The card's heading becomes a real
   * button, so a tap, the keyboard and a screen reader all open it the same
   * way.
   */
  onRowClick?: (row: Row) => void;
  /** Accessible name for the heading button when the heading alone is ambiguous. */
  rowLabel?: (row: Row) => string;
}

/**
 * Table rows as stacked cards — the phone layout shared by
 * {@link ResponsiveTable} and `DataTable`'s `stackOnPhone`.
 *
 * The primary column becomes the card's heading, every other column a
 * label/value pair, and the actions column a row of buttons at the foot. At
 * 375px a six-column table either scrolls sideways (hiding the name and the
 * Edit button behind a scroll nobody knows is there) or crushes every column
 * into unreadable slivers; a card shows all of it, top to bottom.
 */
export function StackedRows<Row>({
  rows,
  columns,
  getRowKey,
  onRowClick,
  rowLabel,
}: StackedRowsProps<Row>) {
  const primary = columns.find((c) => c.primary);
  const fields = columns.filter((c) => !c.primary && !c.actions);
  const actions = columns.filter((c) => c.actions);

  return (
    <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
      {rows.map((row) => (
        <Box
          component="li"
          key={getRowKey(row)}
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
            '&:last-of-type': { borderBottom: 0 },
          }}
        >
          {primary && (
            <Typography component="div" sx={{ fontWeight: 600, fontSize: 14.5, mb: 0.75 }}>
              {onRowClick ? (
                <Box
                  component="button"
                  type="button"
                  aria-label={rowLabel?.(row)}
                  onClick={() => onRowClick(row)}
                  sx={{
                    all: 'unset',
                    cursor: 'pointer',
                    textAlign: 'left',
                    borderRadius: 0.5,
                    '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main' },
                  }}
                >
                  {primary.cell(row)}
                </Box>
              ) : (
                primary.cell(row)
              )}
            </Typography>
          )}
          {fields.length > 0 && (
            <Box
              component="dl"
              sx={{
                display: 'grid',
                gridTemplateColumns: 'minmax(96px, 40%) minmax(0, 1fr)',
                columnGap: 1.5,
                rowGap: 0.5,
                m: 0,
              }}
            >
              {fields.map((column) => (
                <Box key={column.key} sx={{ display: 'contents' }}>
                  <Box
                    component="dt"
                    sx={{ fontSize: 12.5, color: 'text.secondary', overflowWrap: 'anywhere' }}
                  >
                    {column.label}
                  </Box>
                  <Box component="dd" sx={{ m: 0, fontSize: 14, overflowWrap: 'anywhere' }}>
                    {column.cell(row)}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
          {actions.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
              {actions.map((column) => (
                <Box key={column.key} sx={{ display: 'contents' }}>
                  {column.cell(row)}
                </Box>
              ))}
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );
}

export interface ResponsiveTableProps<Row> extends StackedRowsProps<Row> {
  /** Names the table (and the card list) for screen readers. */
  label: string;
}

/**
 * A plain table from `sm` up, stacked cards below it.
 *
 * For the small in-card tables of a settings screen — contractor rates, a fine
 * schedule, statute citations — that do not need `DataTable`'s search, sorting
 * and paging, but must still be usable on a phone. A `DataTable` gets the same
 * phone layout with `stackOnPhone`.
 */
export function ResponsiveTable<Row>({ label, ...props }: ResponsiveTableProps<Row>) {
  const phone = useIsPhone();
  const { rows, columns, getRowKey } = props;

  if (phone) {
    return (
      <Box role="region" aria-label={label}>
        <StackedRows {...props} />
      </Box>
    );
  }

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small" aria-label={label}>
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell key={column.key} align={column.align}>
                {column.actions ? (
                  <Box component="span" sx={visuallyHidden}>
                    {column.label}
                  </Box>
                ) : (
                  column.label
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={getRowKey(row)}>
              {columns.map((column) => (
                <TableCell
                  key={column.key}
                  align={column.align}
                  sx={{
                    ...(column.nowrap || column.actions ? { whiteSpace: 'nowrap' } : null),
                    ...(column.minWidth ? { minWidth: column.minWidth } : null),
                  }}
                >
                  {column.cell(row)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}
